import React, { useRef, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import ChatSessionItem from './ChatSessionItem';
import { useDarkMode } from '../contexts/DarkModeContext';
import { Link, Rocket, Search, X } from 'lucide-react';
import config from '../config.js';
import Tooltip from '../common_jsx/Tooltip';

const Sidebar = ({
  AgentSelector,
  isExpanded,
  onToggle,
  onNewChat,
  sessions,
  activeSessionId,
  onSessionClick,
  onSessionDelete,
  onSessionPin,
  onSessionRename,
  onSessionShare,
  onSessionDownload,
  isLoadingSessions,
  setSelectedScreen,
  onOpenIntegrations,
  onOpenReleaseInfo,
  streamingSessions,
  unreadSessions,
  hasMoreSessions,
  isLoadingMoreSessions,
  loadMoreSessions,
  sharedSessionIds,
}) => {
  const { isDarkMode } = useDarkMode();
  const isProduction = ['prod', 'production'].includes(config.env?.toLowerCase());

  const sentinelRef = useRef(null);
  const loadMoreRef = useRef(loadMoreSessions);
  useEffect(() => { loadMoreRef.current = loadMoreSessions; }, [loadMoreSessions]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMoreSessions) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && typeof loadMoreRef.current === 'function') loadMoreRef.current(); },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMoreSessions]);

  const categorizeSessionsByTime = (sessions) => {
    const now = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;
    const oneWeekMs = 7 * oneDayMs;

    const todayCutoff = new Date();
    todayCutoff.setHours(0, 0, 0, 0);
    const todayTimestamp = todayCutoff.getTime();
    const weekCutoff = now - oneWeekMs;

    return {
      today: sessions.filter((s) => s.update_time >= todayTimestamp),
      thisWeek: sessions.filter((s) => s.update_time >= weekCutoff && s.update_time < todayTimestamp),
      older: sessions.filter((s) => s.update_time < weekCutoff),
    };
  };

  // Titles are generated from the question, so a user who asks similar things
  // ends up with a column of the same sentence and scanning stops working.
  // Filtering is the way back to a specific chat at that point.
  const [query, setQuery] = useState('');
  const filter = query.trim().toLowerCase();

  const visibleSessions = useMemo(
    () => (filter ? sessions.filter((s) => (s.summary || '').toLowerCase().includes(filter)) : sessions),
    [sessions, filter],
  );

  const byRecent = (a, b) => b.update_time - a.update_time;
  const pinnedSessionsList = visibleSessions.filter((s) => s.is_pinned).sort(byRecent);
  const categorized = categorizeSessionsByTime(visibleSessions.filter((s) => !s.is_pinned));
  const hasVisible = pinnedSessionsList.length > 0
    || categorized.today.length > 0
    || categorized.thisWeek.length > 0
    || categorized.older.length > 0;

  // Recedes rather than competing: with a dozen rows on screen the old
  // text-base headings were as loud as the chats they were labelling. Sticky, so
  // scrolling never leaves you wondering which bucket you are in.
  const GROUP_LABEL =
    'sticky top-0 z-10 -mx-2 px-4 pt-2 pb-1 bg-surface text-[11px] font-semibold uppercase tracking-wide text-fg-muted';

  const renderGroup = (label, list, { pinned = false } = {}) => {
    if (!list.length) return null;
    // Sorted here rather than upstream: `chatSessions` is not kept ordered —
    // a live turn updates a session's timestamp in place — so a group would
    // otherwise show an older chat above a newer one, which is exactly what the
    // time column invites you to check.
    const ordered = [...list].sort(byRecent);
    return (
      <section key={label}>
        <h3 className={GROUP_LABEL}>{label}</h3>
        <div className="mt-0.5 space-y-0.5">
          {ordered.map((session) => (
            <ChatSessionItem
              key={session.id}
              id={session.id}
              summary={session.summary}
              updatedAt={session.update_time}
              isActive={activeSessionId === session.id}
              onClick={() => {
                onSessionClick(session.id);
                setSelectedScreen('ChatScreen');
              }}
              onDelete={onSessionDelete}
              onPin={onSessionPin}
              onRename={onSessionRename}
              onShare={onSessionShare}
              onDownloadSession={onSessionDownload}
              isPinned={pinned || (session.is_pinned ?? false)}
              isStreaming={streamingSessions?.has(session.id) ?? false}
              isUnread={unreadSessions?.has(session.id) ?? false}
              isShared={sharedSessionIds?.has(session.id) ?? false}
            />
          ))}
        </div>
      </section>
    );
  };

  // Collapsed sidebar state
  if (!isExpanded) {
    return (
      <div
        className="fixed left-0 top-0 flex flex-col items-center pt-3 px-2 gap-3 h-screen transition-all duration-300 z-[2100] bg-surface border-r border-line"
        style={{ width: '56px' }}
      >
        {/* Expand button with subtle background */}
        <Tooltip text="Expand sidebar" isDarkMode={isDarkMode} direction="right">
          <button
            onClick={onToggle}
            className="flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 bg-surface-raised hover:bg-surface-raised text-fg-muted hover:text-fg shadow-sm"
          >
            {/* Sidebar toggle icon - rectangle with line */}
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="9" y1="3" x2="9" y2="21" />
            </svg>
          </button>
        </Tooltip>

        {/* New Chat button with subtle background */}
        <Tooltip text="New chat" isDarkMode={isDarkMode} direction="right">
          <button
            onClick={onNewChat}
            className="flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 bg-surface-raised hover:bg-surface-raised text-fg-muted hover:text-fg shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </Tooltip>

        {/* Spacer to push integrations to bottom */}
        <div className="flex-1" />

        {/* Integrations button - collapsed */}
        {!isProduction && (
          <Tooltip text="Integrations" isDarkMode={isDarkMode} direction="right">
            <button
              onClick={onOpenIntegrations}
              className="flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 mb-3 bg-surface-raised hover:bg-surface-raised text-fg-muted hover:text-fg shadow-sm"
            >
              <Link className="w-5 h-5" />
            </button>
          </Tooltip>
        )}

        {/* Release Info button - collapsed. Shown in every environment
            (unlike Integrations, which is dev/test-only) -- knowing what's
            deployed is exactly as useful in production. */}
        <Tooltip text="Release Info" isDarkMode={isDarkMode} direction="right">
          <button
            onClick={onOpenReleaseInfo}
            className="flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200 mb-3 bg-surface-raised hover:bg-surface-raised text-fg-muted hover:text-fg shadow-sm"
          >
            <Rocket className="w-5 h-5" />
          </button>
        </Tooltip>
      </div>
    );
  }

  // Expanded sidebar state
  return (
    <div
      className="fixed left-0 top-0 flex flex-col h-screen transition-all duration-300 z-[2100] bg-surface border-r border-line"
      style={{ width: '260px' }}
    >
      {/* Header with collapse button - height matches navbar. The logo renders once, in the
          navbar (src/chat/navbar.jsx), which begins immediately right of this 260px column. */}
      <div className="flex items-center justify-between px-3 h-14">
        {/* The div stays even when empty: it is the left flex item that keeps
            justify-between pushing the collapse button right. */}
        <div className="flex items-center">
          {config.brandShortName && (
            <span className="text-base font-semibold text-fg">
              {config.brandShortName}
            </span>
          )}
        </div>
        {/* Collapse button with subtle background */}
        <Tooltip text="Collapse sidebar" isDarkMode={isDarkMode} direction="right">
          <button
            onClick={onToggle}
            className="flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 bg-surface-raised hover:bg-surface-raised text-fg-muted hover:text-fg"
          >
            {/* Sidebar toggle icon - rectangle with line */}
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="9" y1="3" x2="9" y2="21" />
            </svg>
          </button>
        </Tooltip>
      </div>

      {/* Which agent this workspace is talking to. It lives HERE because
          switching it swaps the session list directly below — cause and effect
          in one column. The component renders nothing on a single-agent
          deployment, so this row simply disappears there. */}
      {AgentSelector && (
        <div className="px-3 pt-3">
          <AgentSelector />
        </div>
      )}

      {/* New Chat Button - soft subtle border */}
      <div className="px-3 py-3">
        <button
          onClick={onNewChat}
          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium rounded-full transition-all duration-200 border border-line hover:border-line hover:bg-surface-raised text-fg-secondary hover:text-fg"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
          </svg>
          <span>New chat</span>
        </button>
      </div>

      {/* Only worth the room once there is enough history to lose something in. */}
      {sessions.length > 7 && (
        <div className="px-3 pb-2">
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-muted"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search chats"
              placeholder="Search chats"
              className="w-full rounded-lg border border-line bg-surface-raised py-1.5 pl-8 pr-7 text-sm text-fg placeholder-fg-muted outline-none transition-colors focus:border-line-strong"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="absolute right-1.5 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center rounded text-fg-muted transition-colors hover:bg-surface hover:text-fg"
              >
                <X aria-hidden="true" className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Chat History */}
      <div className="flex-1 overflow-y-auto px-2 sidebar-scrollbar">
        {isLoadingSessions ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {renderGroup('Pinned', pinnedSessionsList, { pinned: true })}
            {pinnedSessionsList.length > 0 && hasVisible && <div className="border-t border-line" />}
            {renderGroup('Today', categorized.today)}
            {renderGroup('This week', categorized.thisWeek)}
            {renderGroup('Older', categorized.older)}

            {/* An empty list is a place to act, not a place to apologise. */}
            {!hasVisible && filter && (
              <div className="px-3 py-8 text-center">
                <p className="text-sm text-fg-secondary">No chats match “{query.trim()}”.</p>
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="mt-2 text-sm font-medium text-brand hover:underline"
                >
                  Show all chats
                </button>
              </div>
            )}
            {!hasVisible && !filter && (
              <div className="px-3 py-8 text-center">
                <p className="text-sm text-fg-secondary">No chats yet.</p>
                <button
                  type="button"
                  onClick={onNewChat}
                  className="mt-2 text-sm font-medium text-brand hover:underline"
                >
                  Start one
                </button>
              </div>
            )}

            {hasMoreSessions && (
              <div ref={sentinelRef} className="py-4 flex justify-center">
                {isLoadingMoreSessions && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand" />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Integrations button - expanded (at bottom) */}
      {!isProduction && (
        <div className="px-3 py-3 border-t border-line">
          <button
            onClick={onOpenIntegrations}
            className="flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 text-fg-secondary hover:text-fg hover:bg-surface-raised"
          >
            <Link className="w-5 h-5" />
            <span>Integrations</span>
          </button>
        </div>
      )}

      {/* Release Info button - expanded (at bottom). Shown in every
          environment (unlike Integrations, which is dev/test-only).
          When Integrations is also shown just above, its own wrapper
          already contributes py-3 (12px) below it -- pt-0 here avoids
          stacking a second 12px on top of that into one big gap. */}
      <div className={`px-3 pb-3 ${isProduction ? 'pt-3 border-t border-line' : 'pt-0'}`}>
        <button
          onClick={onOpenReleaseInfo}
          className="flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 text-fg-secondary hover:text-fg hover:bg-surface-raised"
        >
          <Rocket className="w-5 h-5" />
          <span>Release Info</span>
        </button>
      </div>
    </div>
  );
};

Sidebar.propTypes = {
  AgentSelector: PropTypes.elementType,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onNewChat: PropTypes.func.isRequired,
  sessions: PropTypes.array.isRequired,
  activeSessionId: PropTypes.string,
  onSessionClick: PropTypes.func.isRequired,
  onSessionDelete: PropTypes.func.isRequired,
  onSessionPin: PropTypes.func.isRequired,
  onSessionRename: PropTypes.func.isRequired,
  onSessionShare: PropTypes.func,
  onSessionDownload: PropTypes.func,
  isLoadingSessions: PropTypes.bool.isRequired,
  setSelectedScreen: PropTypes.func.isRequired,
  onOpenIntegrations: PropTypes.func.isRequired,
  onOpenReleaseInfo: PropTypes.func.isRequired,
  streamingSessions: PropTypes.instanceOf(Set),
  unreadSessions: PropTypes.instanceOf(Set),
  hasMoreSessions: PropTypes.bool,
  isLoadingMoreSessions: PropTypes.bool,
  loadMoreSessions: PropTypes.func,
  sharedSessionIds: PropTypes.instanceOf(Set),
};

export default Sidebar;
